import React, { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle, ChevronLeft, MessageSquareText } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { DATA_SOURCES, BIASES } from "@/data/cardGameData";
import { getCardDisplayTitle } from "@/views/cardgame/cardDisplay";

const copy = {
  nl: {
    compare: "Vergelijk",
    title: "Twee synthetische gebruikers naast elkaar",
    intro: 'Hetzelfde concept, andere kaarten. Let op hoe databronnen, biases en responsmodus veranderen wie de AI "ziet".',
    round: (round) => `Ronde ${round}`,
    noOutput: "_Geen output voor deze ronde._",
    reflectionTitle: "Vergelijk actief",
    reflectionIntro: "Kies een vraag en beschrijf wat je in beide outputs ziet. Zo koppel je je reflectie direct aan concrete verschillen.",
    pickQuestion: "Kies een reflectievraag",
    output1Label: "Ik zie in output 1...",
    output2Label: "Ik zie in output 2...",
    output1Placeholder: "Beschrijf wat opvalt in de eerste output.",
    output2Placeholder: "Beschrijf wat opvalt in de tweede output.",
    saved: "Ingevuld",
    required: "Beantwoord minimaal een vraag voor output 1 en output 2 om door te gaan.",
    back: "Terug",
    continue: "Doorgaan naar reflectie ->",
    answerFirst: "Beantwoord beide outputs om door te gaan",
  },
  en: {
    compare: "Compare",
    title: "Two synthetic users, side by side",
    intro: 'Same concept, different cards. Notice how the data sources, biases and response mode change who the AI "sees".',
    round: (round) => `Round ${round}`,
    noOutput: "_No output for this round._",
    reflectionTitle: "Compare actively",
    reflectionIntro: "Choose a question and describe what you see in both outputs. This connects your reflection to concrete differences.",
    pickQuestion: "Choose a reflection question",
    output1Label: "In output 1, I see...",
    output2Label: "In output 2, I see...",
    output1Placeholder: "Describe what stands out in the first output.",
    output2Placeholder: "Describe what stands out in the second output.",
    saved: "Completed",
    required: "Answer at least one question for output 1 and output 2 to continue.",
    back: "Back",
    continue: "Continue to reflection ->",
    answerFirst: "Answer both outputs to continue",
  },
};

const REFLECTION_QUESTIONS = [
  {
    id: "difference",
    nl: "Welk verschil zie je tussen de twee outputs?",
    en: "What difference do you see between the two outputs?",
  },
  {
    id: "cards",
    nl: "Welke kaart heeft dit verschil waarschijnlijk veroorzaakt?",
    en: "Which card probably caused this difference?",
  },
  {
    id: "assumption",
    nl: "Welke aanname wordt zichtbaar in de output?",
    en: "Which assumption becomes visible in the output?",
  },
  {
    id: "included-excluded",
    nl: "Wie wordt meegenomen of juist buitengesloten?",
    en: "Who is included or excluded?",
  },
  {
    id: "validate",
    nl: "Wat zou je met echte gebruikers moeten valideren?",
    en: "What would you need to validate with real users?",
  },
];

function getCategoryColor(id) {
  if (DATA_SOURCES.find((c) => c.id === id)) return "#5803FD";
  if (BIASES.find((c) => c.id === id)) return "#E85D04";
  return "#63cba4";
}

function getCategoryOrder(id) {
  if (DATA_SOURCES.find((c) => c.id === id)) return 0;
  if (BIASES.find((c) => c.id === id)) return 1;
  return 2;
}

function sortCardsByCategory(cards) {
  return [...(cards || [])].sort((a, b) => getCategoryOrder(a.id) - getCategoryOrder(b.id));
}

const mdComponents = {
  p: ({ node, ...props }) => <p {...props} className="mb-3 text-sm text-black/80 leading-relaxed" />,
  h2: ({ node, ...props }) => <h3 {...props} className="font-display text-sm font-semibold text-black mt-4 mb-1.5" />,
  h3: ({ node, ...props }) => <h4 {...props} className="font-medium text-sm text-black mt-3 mb-1" />,
  ul: ({ node, ...props }) => <ul {...props} className="mt-2 space-y-1.5 pl-1" />,
  li: ({ node, ...props }) => (
    <li className="flex items-start gap-2 text-sm text-black/80">
      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#905cff] shrink-0" />
      <span>{props.children}</span>
    </li>
  ),
  strong: ({ node, ...props }) => <strong {...props} className="font-semibold text-black" />,
  a: ({ node, ...props }) => <a {...props} className="text-[#905cff] underline" target="_blank" rel="noreferrer" />,
};

function RoundPanel({ round, data, color, t, language }) {
  return (
    <div className="rounded-2xl border border-black/10 bg-white p-4 flex flex-col">
      <div className="text-[11px] uppercase tracking-wider font-medium mb-2" style={{ color }}>
        {t.round(round)}
      </div>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {sortCardsByCategory(data?.cards).map((card) => (
          <span
            key={card.id}
            className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium text-white"
            style={{ background: getCategoryColor(card.id) }}
          >
            {getCardDisplayTitle(card, language)}
          </span>
        ))}
      </div>
      <div className="border-t border-black/8 pt-3">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
          {data?.output || t.noOutput}
        </ReactMarkdown>
      </div>
    </div>
  );
}

export function ComparisonStep({
  concept,
  round1,
  round2,
  onNext,
  onBack,
  language = "nl",
  comparisonReflections = [],
  setComparisonReflections,
}) {
  const t = copy[language] || copy.nl;
  const [activeQuestionId, setActiveQuestionId] = useState(null);
  const activeQuestion = REFLECTION_QUESTIONS.find((q) => q.id === activeQuestionId) || null;
  const activeReflection = comparisonReflections.find((r) => r.id === activeQuestionId) || {};
  const hasCompleteReflection = comparisonReflections.some((r) => r.round1?.trim() && r.round2?.trim());

  const completedIds = useMemo(
    () => new Set(comparisonReflections.filter((r) => r.round1?.trim() && r.round2?.trim()).map((r) => r.id)),
    [comparisonReflections]
  );

  const updateReflection = (question, field, value) => {
    if (!setComparisonReflections) return;
    setComparisonReflections((prev) => {
      const existing = prev.find((r) => r.id === question.id);
      const next = {
        id: question.id,
        questionNl: question.nl,
        questionEn: question.en,
        round1: existing?.round1 || "",
        round2: existing?.round2 || "",
        [field]: value,
      };
      if (existing) {
        return prev.map((r) => (r.id === question.id ? next : r));
      }
      return [...prev, next];
    });
  };

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-black/10 bg-white p-5">
        <div className="text-[11px] uppercase tracking-wider text-[#905cff] font-medium mb-1">{t.compare}</div>
        <h2 className="font-display text-xl font-semibold text-black">{t.title}</h2>
        <p className="text-sm text-black/50 mt-1">
          {t.intro}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
        <RoundPanel round={1} data={round1} color="#5803FD" t={t} language={language} />
        <RoundPanel round={2} data={round2} color="#E85D04" t={t} language={language} />
      </div>

      <div className="rounded-2xl border border-black/10 bg-white p-5">
        <div className="flex items-center gap-2 text-[#905cff] mb-2">
          <MessageSquareText className="h-4 w-4" />
          <div className="text-[11px] uppercase tracking-wider font-medium">{t.reflectionTitle}</div>
        </div>
        <p className="text-sm text-black/60 leading-relaxed mb-4">{t.reflectionIntro}</p>

        <div className="text-xs text-black/40 uppercase tracking-wider mb-2">{t.pickQuestion}</div>
        <div className="flex flex-wrap gap-2 mb-4">
          {REFLECTION_QUESTIONS.map((question) => {
            const completed = completedIds.has(question.id);
            const active = activeQuestionId === question.id;
            return (
              <button
                key={question.id}
                type="button"
                onClick={() => setActiveQuestionId(question.id)}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors ${
                  active
                    ? "border-[#905cff] bg-[#905cff] text-white"
                    : "border-black/10 bg-white text-black/70 hover:border-[#905cff]/50"
                }`}
              >
                {completed && <CheckCircle className="h-3.5 w-3.5" />}
                {question[language] || question.nl}
              </button>
            );
          })}
        </div>

        {activeQuestion ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-black mb-1">{t.output1Label}</label>
              <Textarea
                value={activeReflection.round1 || ""}
                onChange={(event) => updateReflection(activeQuestion, "round1", event.target.value)}
                placeholder={t.output1Placeholder}
                rows={4}
                className="rounded-xl resize-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-black mb-1">{t.output2Label}</label>
              <Textarea
                value={activeReflection.round2 || ""}
                onChange={(event) => updateReflection(activeQuestion, "round2", event.target.value)}
                placeholder={t.output2Placeholder}
                rows={4}
                className="rounded-xl resize-none"
              />
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-black/15 bg-black/[0.02] p-4 text-sm text-black/55">
            {t.required}
          </div>
        )}

        {!hasCompleteReflection && (
          <p className="mt-3 text-xs text-black/45">{t.required}</p>
        )}
      </div>

      <div className="flex gap-3">
        <Button onClick={onBack} variant="outline" className="rounded-xl flex-none">
          <ChevronLeft className="h-4 w-4 mr-1" /> {t.back}
        </Button>
        <Button
          onClick={onNext}
          disabled={!hasCompleteReflection}
          className="flex-1 rounded-xl bg-[#905cff] text-white hover:bg-[#5802ff] disabled:opacity-50 py-3"
        >
          {hasCompleteReflection ? t.continue : t.answerFirst}
        </Button>
      </div>
    </div>
  );
}
